import React, { useState, useEffect } from 'react';

import {
  AssetId,
  GroupId,
  Asset,
  Group,
  isUncategorizedId,
} from 'hooks/useGrouping';
import AssetItem from './AssetItem';
import { BulkPopupMenu } from './PopupMenu';
import SlideOut from './SlideOut';
import { TransitionGroup } from 'react-transition-group';

type AssetListProps = {
  groupFilteredAssets: Asset[];
  groups: Group[];
  changeGroup: (assetIds: AssetId[], groupId: GroupId) => void;
  currentGroupId: GroupId;
  isTabChanged: boolean;
  isTabReady: boolean;
  finishTabChange: () => void;
};

const SELECT_MODE = {
  NORMAL: 'NORMAL',
  BULK_SELECT: 'BULK_SELECT',
  SHOW_BULK_SELECT_POPUP: 'SHOW_BULK_SELECT_POPUP',
} as const;
const isSelect = (
  state:
    | { selectMode: typeof SELECT_MODE.NORMAL }
    | {
        selectMode: typeof SELECT_MODE.BULK_SELECT;
        selectedAssetIds: AssetId[];
        showBulkSelectPopupMenu: false;
      }
    | {
        selectMode: typeof SELECT_MODE.SHOW_BULK_SELECT_POPUP;
        selectedAssetIds: AssetId[];
        showBulkSelectPopupMenu: true;
      }
): state is
  | {
      selectMode: typeof SELECT_MODE.BULK_SELECT;
      selectedAssetIds: AssetId[];
      showBulkSelectPopupMenu: false;
    }
  | {
      selectMode: typeof SELECT_MODE.SHOW_BULK_SELECT_POPUP;
      selectedAssetIds: AssetId[];
      showBulkSelectPopupMenu: true;
    } => {
  return state.selectMode !== SELECT_MODE.NORMAL;
};

const AssetList: React.FC<AssetListProps> = props => {
  const [assetSelect, setAssetSelect] = useState<
    | { selectMode: typeof SELECT_MODE.NORMAL }
    | {
        selectMode: typeof SELECT_MODE.BULK_SELECT;
        selectedAssetIds: AssetId[];
        showBulkSelectPopupMenu: false;
      }
    | {
        selectMode: typeof SELECT_MODE.SHOW_BULK_SELECT_POPUP;
        selectedAssetIds: AssetId[];
        showBulkSelectPopupMenu: true;
      }
  >({ selectMode: SELECT_MODE.NORMAL });
  // 表示する個別のポップアップメニューのid。なにか一つ表示 or 何も表示しない
  const [
    showPopupMenuAssetId,
    setShowPopupMenuAssetId,
  ] = useState<AssetId | null>(null);
  useEffect(() => {
    if (props.isTabChanged) {
      // タブが切り替わったら、popupmenuを閉じ、一括操作を終了
      setShowPopupMenuAssetId(null);
      endBulkSelect();
    }
  }, [props.isTabChanged]);
  useEffect(() => {
    if (
      // ここは列挙するほうが見やすくミスが少ない
      assetSelect.selectMode === SELECT_MODE.BULK_SELECT ||
      assetSelect.selectMode === SELECT_MODE.SHOW_BULK_SELECT_POPUP
    ) {
      // 一括操作が始まったら、popupmenuを閉じる
      setShowPopupMenuAssetId(null);
    }
  }, [assetSelect.selectMode]);
  useEffect(() => {
    props.finishTabChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.currentGroupId.id]);
  useEffect(() => {
    if (props.groupFilteredAssets.length === 0) {
      endBulkSelect();
    }
  }, [props.groupFilteredAssets.length]);
  const toggleSelect = (assetId: AssetId): void => {
    setAssetSelect(prev => {
      if (!isSelect(prev)) return prev;
      if (prev.selectedAssetIds.includes(assetId)) {
        return {
          ...prev,
          selectedAssetIds: prev.selectedAssetIds.filter(id => id !== assetId),
        };
      } else {
        return {
          ...prev,
          selectedAssetIds: [...prev.selectedAssetIds, assetId],
        };
      }
    });
  };
  const hideBulkPopupMenu = (): void => {
    setAssetSelect(prev => {
      if (prev.selectMode === SELECT_MODE.SHOW_BULK_SELECT_POPUP)
        return {
          ...prev,
          selectMode: SELECT_MODE.BULK_SELECT,
          showBulkSelectPopupMenu: false,
        };
      return prev;
    });
  };
  const endBulkSelect = (): void => {
    setAssetSelect({ selectMode: SELECT_MODE.NORMAL });
  };
  const bulkOperationButtonClickHandler = (
    event: React.MouseEvent,
    isDisabled: boolean
  ): void => {
    if (isDisabled) return;
    setAssetSelect(prev => {
      if (prev.selectMode === SELECT_MODE.NORMAL) {
        return {
          ...prev,
          selectMode: SELECT_MODE.BULK_SELECT,
          showBulkSelectPopupMenu: false,
          selectedAssetIds: [],
        };
      } else if (prev.selectMode === SELECT_MODE.BULK_SELECT) {
        return {
          ...prev,
          selectMode: SELECT_MODE.SHOW_BULK_SELECT_POPUP,
          showBulkSelectPopupMenu: true,
        };
      } else {
        return {
          ...prev,
          selectMode: SELECT_MODE.BULK_SELECT,
          showBulkSelectPopupMenu: false,
        };
      }
    });
  };
  const noItemMessage = isUncategorizedId(props.currentGroupId)
    ? '未分類の口座はございません。'
    : 'このグループには口座が登録されていません。';

  const noItem = (
    <div className="grouping-tab-body-item no-item">
      <div className="grouping-tab-body-message">
        <div className="grouping-tab-body-primary-title">{noItemMessage}</div>
        <div className="grouping-tab-body-note">
          資産のグループ分けは口座情報の右側の「
          <i className="icon-more-vertical"></i>
          」から操作することができます。
        </div>
        <div className="grouping-tab-body-note">
          口座を資産ごとにグループにまとめて管理すれば自分のお金がもっと“見える化”できます。
        </div>
      </div>
    </div>
  );

  const allBulkSelectSwitch = (): void => {
    if (!props.groupFilteredAssets.length) return;
    setAssetSelect(prev => {
      if (!isSelect(prev)) return prev;
      if (prev.selectedAssetIds.length) {
        return {
          ...prev,
          selectedAssetIds: [],
        };
      } else {
        return {
          ...prev,
          selectedAssetIds: props.groupFilteredAssets.map(asset => asset.id),
        };
      }
    });
  };

  const bulkPopupMenuItemClickHandler = async (
    groupId: GroupId
  ): Promise<void> => {
    if (!isSelect(assetSelect)) return;
    props.changeGroup(assetSelect.selectedAssetIds, groupId);
    setAssetSelect(prev => {
      if (!isSelect(prev)) return prev;
      return {
        ...prev,
        selectedAssetIds: [],
      };
    });
  };

  const isAllChecked =
    isSelect(assetSelect) &&
    props.groupFilteredAssets
      .map(asset => asset.id)
      .filter(id => !assetSelect.selectedAssetIds.includes(id)).length === 0;

  const itemChangeGroup = (asset: Asset) => {
    return async (groupId: GroupId): Promise<void> =>
      props.changeGroup([asset.id], groupId);
  };

  const shouldClosePopupMenuFromPoint = (event: React.MouseEvent): boolean => {
    const element = document.elementFromPoint(event.clientX, event.clientY);
    if (
      element?.classList.contains('check-col') ||
      element?.classList.contains('icon-check')
    ) {
      return false;
    }
    return true;
  };

  const bulkOperationButton =
    assetSelect.selectMode === SELECT_MODE.NORMAL ? (
      <>一括操作</>
    ) : assetSelect.selectMode === SELECT_MODE.BULK_SELECT ? (
      <>
        <span className="bulk-select-text">一括操作</span>
        <i className="bulk-select-icon icon-arrow_right" />
      </>
    ) : (
      <>
        <span className="bulk-select-text">一括操作</span>
        <i className="bulk-select-icon icon-arrow_drop_down" />
      </>
    );

  const bulkOperationButtonClass = `grouping-tab-control-list-item bulk-operation ${
    !props.groupFilteredAssets.length ? 'disabled' : 'cursor-pointer'
  } ${
    assetSelect.selectMode === SELECT_MODE.BULK_SELECT ? 'bulk-select' : ''
  } ${
    assetSelect.selectMode === SELECT_MODE.SHOW_BULK_SELECT_POPUP
      ? 'show-bulk-select'
      : ''
  }`;

  return (
    <>
      <div className="grouping-tab-control-row">
        <ul className="grouping-tab-control-list bulk">
          {isSelect(assetSelect) && (
            <li
              className={
                'grouping-tab-control-list-item bulk ' +
                (!props.groupFilteredAssets.length
                  ? ' disabled'
                  : 'cursor-pointer')
              }
              onClick={allBulkSelectSwitch}>
              <div className="check-col">
                <i
                  className={
                    'icon-check' + (isAllChecked ? ' is-active' : '')
                  }></i>
              </div>
              一括選択
            </li>
          )}
          <li
            className={bulkOperationButtonClass}
            onClick={event =>
              bulkOperationButtonClickHandler(
                event,
                props.groupFilteredAssets.length === 0
              )
            }>
            {bulkOperationButton}
            {isSelect(assetSelect) && (
              <BulkPopupMenu
                showPopupMenu={assetSelect.showBulkSelectPopupMenu}
                closeHandlers={{
                  onClose: hideBulkPopupMenu,
                  shouldClosePopupMenuFromPoint: shouldClosePopupMenuFromPoint,
                }}
                onFinishedBulkOperation={event => {
                  event.stopPropagation();
                  endBulkSelect();
                }}
                groups={props.groups}
                currentGroupId={props.currentGroupId}
                onPopupMenuItemClick={bulkPopupMenuItemClickHandler}
              />
            )}
          </li>
        </ul>
      </div>
      <ul
        className={'asset-list' + (isSelect(assetSelect) ? ' is-select' : '')}>
        {!props.groupFilteredAssets.length && noItem}
        {
          <TransitionGroup component={null}>
            {props.groupFilteredAssets.map(asset => {
              return (
                <SlideOut
                  duration={props.isTabChanged || props.isTabReady ? 500 : 0}
                  key={asset.id}>
                  <AssetItem
                    changeGroup={itemChangeGroup(asset)}
                    asset={asset}
                    groups={props.groups}
                    showPopupMenu={showPopupMenuAssetId === asset.id}
                    setShowPopupMenuAssetId={setShowPopupMenuAssetId}
                    select={
                      isSelect(assetSelect)
                        ? {
                            isSelect: true,
                            selected: assetSelect.selectedAssetIds.includes(
                              asset.id
                            ),
                            toggleSelect: (event: React.MouseEvent): void => {
                              event.stopPropagation();
                              toggleSelect(asset.id);
                            },
                          }
                        : { isSelect: false }
                    }
                  />
                </SlideOut>
              );
            })}
          </TransitionGroup>
        }
      </ul>
    </>
  );
};

export default AssetList;
